import { createEffect, createStore, createEvent, combine } from 'effector';
import { createFetching } from '@lib/effector-utils';

import { fetchOrganizationChart } from '../api';

export const fetchOrganizationEffect = createEffect('fetch organization chart');
fetchOrganizationEffect.use(fetchOrganizationChart);

export const changeOrganizationSearch = createEvent('organization search');
export const changeOrganizationDepartment = createEvent(
  'organization department filter',
);

export const $organizationFetching = createFetching(fetchOrganizationEffect);

export const $organization = createStore({
  totalEmployees: 0,
  departments: [],
  employees: [],
}).on(fetchOrganizationEffect.done, (state, { result }) =>
  result && result.data ? result.data : state,
);

export const $organizationSearch = createStore('').on(
  changeOrganizationSearch,
  (_, value) => value,
);

/** Department id, or '' for all departments. */
export const $organizationDepartment = createStore('').on(
  changeOrganizationDepartment,
  (_, value) => value,
);

function matchesSearch(employee, search) {
  if (!search) return true;
  return [employee.fullName, employee.jobTitle, employee.email]
    .filter(Boolean)
    .some(text => text.toLowerCase().includes(search));
}

/**
 * Departments with their filtered employees: department heads first, then by
 * name. Employees outside the listed departments are grouped last.
 */
export const $organizationGroups = combine(
  $organization,
  $organizationSearch,
  $organizationDepartment,
  ({ departments, employees }, searchValue, departmentId) => {
    const search = searchValue.trim().toLowerCase();
    const knownIds = departments.map(department => department.id);
    const groups = departments.concat([{ id: null, name: null, headIds: [] }]);

    return groups
      .filter(group => departmentId === '' || group.id === departmentId)
      .map(group => {
        const headIds = group.headIds || [];
        const members = employees
          .filter(employee =>
            group.id === null
              ? !knownIds.includes(employee.departmentId)
              : employee.departmentId === group.id,
          )
          .filter(employee => matchesSearch(employee, search))
          .map(employee => ({
            ...employee,
            isHead: headIds.includes(employee.id),
          }))
          .sort(
            (a, b) =>
              Number(b.isHead) - Number(a.isHead) ||
              (a.fullName || '').localeCompare(b.fullName || ''),
          );

        return { ...group, employees: members };
      })
      .filter(group => group.employees.length > 0);
  },
);
